import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { Contract } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface ScheduleRow {
    due_date: string;
    balance: string;
    minimum_payment: string;
    interest_portion: string;
    principal_portion: string;
    fees_portion: string;
    remaining_balance: string;
}

export default function AmortizationPage() {
    const { id } = useParams();

    const { data: contractData, isLoading: contractLoading } = useQuery({
        queryKey: ['contract', id],
        queryFn: () => api.get<{ data: Contract }>(`/contracts/${id}`),
    });

    const { data: scheduleData, isLoading: scheduleLoading } = useQuery({
        queryKey: ['amortization', id],
        queryFn: () => api.get<{ data: ScheduleRow[] }>(`/contracts/${id}/amortization`),
    });

    const contract = contractData?.data;
    const schedule = scheduleData?.data ?? [];
    const isLoading = contractLoading || scheduleLoading;

    if (isLoading) return <p className="text-sm text-slate-400">Loading...</p>;
    if (!contract) return <p className="text-sm text-red-400">Contract not found.</p>;

    return (
        <div className="space-y-6 pb-20">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-semibold">Debt Schedule</h1>
                    <p className="text-sm text-slate-400">{contract.title}</p>
                </div>
                <Link to={`/contracts/${id}`}>
                    <Button variant="outline" size="sm">Back to Contract</Button>
                </Link>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Card>
                    <CardContent className="p-3 text-center">
                        <p className="text-[10px] text-slate-400">Current Balance</p>
                        <p className="text-sm font-semibold text-red-400">{formatCurrency(contract.current_balance)}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-3 text-center">
                        <p className="text-[10px] text-slate-400">Min Payment</p>
                        <p className="text-sm font-semibold">{formatCurrency(contract.minimum_payment)}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-3 text-center">
                        <p className="text-[10px] text-slate-400">Interest Rate</p>
                        <p className="text-sm font-semibold">{contract.interest_rate}%</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-3 text-center">
                        <p className="text-[10px] text-slate-400">Total If Min Only</p>
                        <p className="text-sm font-semibold text-violet-400">
                            {formatCurrency(schedule.reduce((sum, r) => sum + (parseFloat(r.minimum_payment) || 0), 0))}
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Schedule table */}
            {schedule.length === 0 ? (
                <Card>
                    <CardContent className="py-8 text-center text-sm text-slate-400">
                        No amortization schedule available.
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Payment Schedule</CardTitle>
                    </CardHeader>
                    <CardContent className="overflow-x-auto p-0">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-800 text-left text-xs text-slate-400">
                                    <th className="px-4 py-3 font-medium">Due Date</th>
                                    <th className="px-4 py-3 font-medium text-right">Balance</th>
                                    <th className="px-4 py-3 font-medium text-right">Payment</th>
                                    <th className="hidden px-4 py-3 font-medium text-right sm:table-cell">Interest</th>
                                    <th className="hidden px-4 py-3 font-medium text-right sm:table-cell">Principal</th>
                                    <th className="hidden px-4 py-3 font-medium text-right sm:table-cell">Fees</th>
                                    <th className="px-4 py-3 font-medium text-right">Remaining</th>
                                    <th className="px-4 py-3 font-medium text-right">Total Paid</th>
                                </tr>
                            </thead>
                            <tbody>
                                {schedule.reduce<{ rows: React.ReactNode[]; cumulative: number }>((acc, row, i) => {
                                    const payment = parseFloat(row.minimum_payment) || 0;
                                    acc.cumulative += payment;
                                    acc.rows.push(
                                    <tr
                                        key={i}
                                        className="border-b border-slate-800/50 transition-colors hover:bg-slate-800/30"
                                    >
                                        <td className="px-4 py-2 text-slate-300">
                                            {formatDate(row.due_date)}
                                        </td>
                                        <td className="px-4 py-2 text-right font-mono text-red-400">
                                            {formatCurrency(row.balance)}
                                        </td>
                                        <td className="px-4 py-2 text-right font-mono">
                                            {formatCurrency(row.minimum_payment)}
                                        </td>
                                        <td className="hidden px-4 py-2 text-right font-mono text-orange-400 sm:table-cell">
                                            {formatCurrency(row.interest_portion)}
                                        </td>
                                        <td className="hidden px-4 py-2 text-right font-mono text-emerald-400 sm:table-cell">
                                            {formatCurrency(row.principal_portion)}
                                        </td>
                                        <td className="hidden px-4 py-2 text-right font-mono text-red-400 sm:table-cell">
                                            {parseFloat(row.fees_portion) > 0 ? formatCurrency(row.fees_portion) : '—'}
                                        </td>
                                        <td className="px-4 py-2 text-right font-mono text-slate-300">
                                            {formatCurrency(row.remaining_balance)}
                                        </td>
                                        <td className="px-4 py-2 text-right font-mono text-violet-400">
                                            {formatCurrency(acc.cumulative)}
                                        </td>
                                    </tr>
                                    );
                                    return acc;
                                }, { rows: [], cumulative: 0 }).rows}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
